// @ts-check

import { get, getAll } from "@/utilities/querySelector";

/**
 * It's not a true web component as it requires specific HTML content to actually function
 * but it's a neat way of packing up the functionality IMO
 */
export class TabGroupHTMLElement extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.state = 'desktop';
        this.tabsWrap = get( '[data-tabs]', this );
        this.panelsWrap = get( '[data-panels]', this );
        this.tabs = getAll( '[data-tab]', this );
        this.panels = getAll( '[data-panel]', this );
        this.breakpoint = window.matchMedia( '(min-width: 1024px)' );
        this.header = document.querySelector('header');  // Add this line

        this.tabs.forEach( tab => {
            tab.addEventListener( 'click', this.onTabClick.bind( this ) );
        } );

        this.assignMode();
        this.breakpoint.addEventListener( 'change', this.assignMode.bind( this ) );
        this.setAttribute( 'data-initialized', '' );
    }

    /**
     * @param {MouseEvent} e
     *
     * @return void
     */
    onTabClick( e ) {
        const tab = e.currentTarget;

        if ( !( tab instanceof HTMLElement ) ) return;

        const isActive = tab.hasAttribute('data-active');
        const isMobile = !this.breakpoint.matches;

        this.tabs.forEach( tab => tab.removeAttribute( 'data-active' ) );
        tab.setAttribute( 'data-active', '' );

        const panel = this.getTabsPanel( tab );
        if ( !( panel instanceof HTMLElement ) ) return;

        if (isMobile && isActive) {
            e.preventDefault();
            this.tabs.forEach(tab => tab.hasAttribute( 'data-active' ) ? tab.removeAttribute( 'data-active' ) : null );
            this.deactivate(panel);
            return;
        }

        const activepanel = get( '[data-panel][data-active]', this );

        if ( activepanel === panel ) return;

        e.preventDefault();

        if ( activepanel ) {
            const anim = this.deactivate( activepanel );
            anim.addEventListener( 'finish', () => {
                this.activate( panel );
            } );
        } else {
            this.activate( panel );
        }
    }

    assignMode() {
        if ( this.breakpoint.matches ) {
            this.setDesktopMode();
        } else {
            this.setMobileMode();
        }
    }

    setMobileMode() {
        this.tabs.forEach( tab => {
            const panel = this.getTabsPanel( tab );
            tab.after( panel );
        } );
        this.state = 'mobile';
    }

    setDesktopMode() {
        const tabsArray = Array.from(this.tabs);

        tabsArray.forEach( tab => {
            const panel = this.getTabsPanel( tab );
            tab.after( panel );
            this.panelsWrap.appendChild( panel );
        } );

        const activeTab = tabsArray.find(tab => tab.hasAttribute('data-active'));
        if (!activeTab && tabsArray.length > 0) {
            tabsArray[0].setAttribute('data-active', 'true');
        }

        this.state = 'desktop';
    }

    /**
     *
     * @param {HTMLElement} tab
     *
     * @returns {HTMLElement|undefined}
     */
    getTabsPanel( tab ) {
        const id = tab.dataset.tab;
        return get( `[data-panel="${id}"]`, this );
    }

    /**
     *
     * To be used externally for whatever reason
     *
     * @param {string} id The name associate with the panel
     */
    goTo( id ) {
        const tab = get( `[data-tab="${id}"]`, this );
        const panel = get( `[data-panel="${id}"]`, this );

        if ( !( panel instanceof HTMLElement ) ) return;

        this.tabs.forEach( tab => tab.removeAttribute( 'data-active' ) );
        tab.setAttribute( 'data-active', '' );

        const activepanel = get( '[data-panel][data-active]', this );

        if ( activepanel === panel ) return panel?.scrollIntoView();

        const anim = this.deactivate( activepanel );
        anim.addEventListener( 'finish', () => {
            this.activate( panel );
            panel?.scrollIntoView();
        } );
    }

    /**
     * @param {HTMLElement} panel
     *
     * @return {Animation}
     */
    deactivate( panel ) {
        if (panel.hasAttribute( 'data-active' )) {
            panel.removeAttribute( 'data-active' );
        }
        // Return a dummy animation so the promise chain still works
        return panel.animate({}, { duration: 0 });
    }

    /**
     * @param {HTMLElement} panel
     *
     * @return {Animation}
     */
    activate( panel ) {
        panel.setAttribute( 'data-active', '' );

        if (this.state === 'desktop' && this.panelsWrap.scrollTo) {
            this.panelsWrap.scrollTo({
                left: panel.offsetLeft,
                top: 0,
                behavior: 'smooth'
            });
        } else if (this.state === 'mobile') {
            const headerHeight = this.header ? this.header.offsetHeight : 0;
            const panelTop = panel.getBoundingClientRect().top + window.pageYOffset - (headerHeight + 50);
            window.scrollTo({
                top: panelTop,
                behavior: 'smooth'
            });
        }

        // Return a dummy animation so the promise chain still works
        return panel.animate({}, { duration: 0 });
    }
}

if ( !customElements.get( 'tab-group' ) ) {
    customElements.define( 'tab-group', TabGroupHTMLElement );
}
